import { useEffect, useState } from 'preact/hooks';
import { api, ApiError } from './api';
import { deviceCountry } from './region';
import type { AlbumCard, Friend, Lang, Postcard, Series, Stamp, User } from './types';

export type Tab = 'mailbox' | 'track' | 'compose' | 'album' | 'profile';

/** A postcard being written. As in the design it has two steps: front, then message. */
export interface ComposeDraft {
  step: 0 | 1;
  frontMedia: string | null;
  frontPreview: string | null;
  noteMode: 'type' | 'scan';
  noteText: string;
  noteMedia: string | null;
  notePreview: string | null;
  stamp: string | null;
  recipient: string;
  toCountry: string;
}

export function emptyDraft(): ComposeDraft {
  return {
    step: 0,
    frontMedia: null,
    frontPreview: null,
    noteMode: 'type',
    noteText: '',
    noteMedia: null,
    notePreview: null,
    stamp: null,
    recipient: '',
    toCountry: '',
  };
}

export interface AppState {
  ready: boolean;
  user: User | null;
  lang: Lang;
  tab: Tab;

  mailbox: Postcard[];
  outgoing: Postcard[];
  friends: Friend[];
  stamps: Stamp[];
  stampCountry: string;
  allStamps: Stamp[];
  series: Series[];
  album: AlbumCard[];

  pile: number;
  openCardId: string | null;
  cardSide: 'front' | 'back';

  draft: ComposeDraft;
  uploading: 'front' | 'note' | null;
  sentCard: Postcard | null;

  pickerOpen: boolean;
  addressOpen: boolean;
  zoomIndex: number | null;
  confirmCancelId: string | null;
  editOpen: boolean;

  toast: { text: string; kind: 'info' | 'error' } | null;
}

let state: AppState = {
  ready: false,
  user: null,
  lang: 'es',
  tab: 'mailbox',
  mailbox: [],
  outgoing: [],
  friends: [],
  stamps: [],
  stampCountry: deviceCountry(),
  allStamps: [],
  series: [],
  album: [],
  pile: 0,
  openCardId: null,
  cardSide: 'front',
  draft: emptyDraft(),
  uploading: null,
  sentCard: null,
  pickerOpen: false,
  addressOpen: false,
  zoomIndex: null,
  confirmCancelId: null,
  editOpen: false,
  toast: null,
};

type Listener = (s: AppState) => void;
const listeners = new Set<Listener>();
let toastTimer: number | undefined;

export function getState(): AppState {
  return state;
}

export function set(patch: Partial<AppState>): void {
  state = { ...state, ...patch };
  for (const fn of listeners) fn(state);
}

export function setDraft(patch: Partial<ComposeDraft>): void {
  set({ draft: { ...state.draft, ...patch } });
}

/** Re-renders the calling component whenever the store changes. */
export function useAppState(): AppState {
  const [s, setS] = useState(state);
  useEffect(() => {
    listeners.add(setS);
    setS(state);
    return () => {
      listeners.delete(setS);
    };
  }, []);
  return s;
}

export function toast(text: string, kind: 'info' | 'error' = 'info'): void {
  set({ toast: { text, kind } });
  window.clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => set({ toast: null }), 4000);
}

/** Turns anything thrown into a message worth showing a person. */
export function errorText(err: unknown, fallback: string): string {
  if (err instanceof ApiError) return err.message;
  if (err instanceof Error && err.message) return err.message;
  return fallback;
}

// ------------------------------------------------------------------ loading

export async function bootstrap(): Promise<void> {
  try {
    const { user } = await api.me();
    set({ user, lang: user.lang, ready: true });
    await refreshAll();
  } catch (err) {
    set({ ready: true });
    if (err instanceof ApiError && err.status === 401) {
      set({ user: null });
      return;
    }
    throw err;
  }
}

export async function refreshAll(): Promise<void> {
  const [mail, transit, friends, stamps, album] = await Promise.allSettled([
    api.mailbox(),
    api.transit(),
    api.friends(),
    api.stamps(deviceCountry()),
    api.album(),
  ]);

  const patch: Partial<AppState> = {};
  if (mail.status === 'fulfilled') patch.mailbox = mail.value.cards;
  if (transit.status === 'fulfilled') patch.outgoing = transit.value.outgoing;
  if (friends.status === 'fulfilled') {
    patch.friends = friends.value.friends;
  }
  if (stamps.status === 'fulfilled') {
    patch.stamps = stamps.value.stamps;
    patch.stampCountry = stamps.value.country;
    patch.allStamps = stamps.value.all;
    patch.series = stamps.value.series;
  }
  if (album.status === 'fulfilled') patch.album = album.value.album;
  set(patch);
}

export async function refreshTransit(): Promise<void> {
  try {
    const { outgoing } = await api.transit();
    set({ outgoing });
  } catch {
    // A failed background poll is not worth interrupting anyone over.
  }
}

/** Resolves a stamp id against the full catalogue the server sent. */
export function stampById(id: string | null): Stamp | null {
  if (!id) return null;
  return state.allStamps.find((s) => s.id === id) ?? state.stamps.find((s) => s.id === id) ?? null;
}
